import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Drawer,
  useMediaQuery,
  useTheme,
  Fab,
  Skeleton,
  Alert,
  Snackbar
} from '@mui/material';
import { FilterList, Map as MapIcon } from '@mui/icons-material';
import SearchBar from '../components/SearchBar';
import FiltersPanel from '../components/FiltersPanel';
import MissionCard from '../components/MissionCard';
import MissionDetailModal from '../components/MissionDetailModal';
import MapView from '../components/MapView';
import Pagination from '../components/Pagination';
import Breadcrumbs from '../components/Breadcrumbs';
import { 
  fetchMissions, 
  selectMissions, 
  selectMissionsLoading, 
  selectMissionsError, 
  selectMissionsPagination,
  selectSelectedMission,
  toggleFavorite, 
  setPagination, 
  setSortBy,
  setFilters,
  setSelectedMission
} from '../redux/slices/missionsSlice';
import './Missions.css';

const Missions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  
  const dispatch = useDispatch();
  const loading = useSelector(selectMissionsLoading);
  const error = useSelector(selectMissionsError);
  const missions = useSelector(selectMissions);
  const pagination = useSelector(selectMissionsPagination);
  const selectedMission = useSelector(selectSelectedMission);
  const { sortBy } = useSelector(state => state.missions);
  
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Fetch missions on mount
  useEffect(() => {
    dispatch(fetchMissions());
  }, [dispatch]);
  
  // Handle search from SearchBar
  const handleSearch = (searchData) => {
    dispatch(setFilters(searchData));
    dispatch(setPagination({ ...pagination, currentPage: 1 }));
    dispatch(fetchMissions());
  };
  
  // Handle mission card click
  const handleMissionClick = (mission) => {
    dispatch(setSelectedMission(mission));
    setDetailModalOpen(true);
  };
  
  // Handle favorite toggle
  const handleFavoriteToggle = (missionId) => {
    setSnackbar({
      open: true,
      message: 'Mission ajoutée aux favoris',
      severity: 'success'
    });
  };
  
  // Handle apply to mission
  const handleApply = (missionId) => {
    setSnackbar({
      open: true,
      message: 'Candidature envoyée avec succès',
      severity: 'success'
    });
  };
  
  // Handle share mission
  const handleShare = (mission) => {
    if (navigator.share) {
      navigator.share({
        title: mission.title,
        text: mission.description,
        url: window.location.href + '/' + mission.id
      });
    } else {
      navigator.clipboard.writeText(window.location.href + '/' + mission.id);
      setSnackbar({
        open: true,
        message: 'Lien copié dans le presse-papiers',
        severity: 'success'
      });
    }
  };
  
  // Handle page change
  const handlePageChange = (page) => {
    dispatch(setPagination({ ...pagination, currentPage: page }));
    dispatch(fetchMissions());
  };
  
  // Handle sort change
  const handleSortChange = (event) => {
    dispatch(setSortBy(event.target.value));
    dispatch(fetchMissions());
  };
  
  // Sort missions - ensure missions is an array
  const sortedMissions = Array.isArray(missions) ? [...missions].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(a.date) - new Date(b.date);
      case 'rate-asc':
        return parseInt(a.rate.replace(/[^\d]/g, '')) - parseInt(b.rate.replace(/[^\d]/g, ''));
      case 'rate-desc':
        return parseInt(b.rate.replace(/[^\d]/g, '')) - parseInt(a.rate.replace(/[^\d]/g, ''));
      default:
        return 0;
    }
  }) : [];
  
  // Use paginated missions from Redux state - ensure it's an array
  const paginatedMissions = Array.isArray(missions) ? missions : [];
  
  // Render skeleton cards
  const renderSkeletonCards = () => {
    const skeletonCount = isMobile ? 6 : 12;
    return Array.from({ length: skeletonCount }).map((_, index) => (
      <Grid item xs={12} sm={6} lg={4} key={index}>
        <Box className="mission-card-skeleton">
          <Skeleton variant="rectangular" height={200} />
          <Box sx={{ p: 2 }}>
            <Skeleton variant="text" height={32} />
            <Skeleton variant="text" height={20} width="80%" />
            <Skeleton variant="text" height={20} width="60%" />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Skeleton variant="text" width={80} />
              <Skeleton variant="rectangular" width={120} height={36} />
            </Box>
          </Box>
        </Box>
      </Grid>
    ));
  };
  
  return (
    <>
      <Helmet>
        <title>Missions Sportives - Mission Sport</title>
        <meta name="description" content="Découvrez toutes les missions sportives disponibles. Trouvez l'opportunité parfaite selon votre discipline et votre localisation." />
        <meta property="og:title" content="Missions Sportives - Mission Sport" />
        <meta property="og:description" content="Découvrez toutes les missions sportives disponibles. Trouvez l'opportunité parfaite selon votre discipline et votre localisation." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
      </Helmet>
      
      <Box className="missions-page">
        {/* Hero Section with Search */}
        <Box className="missions-hero">
          <Container maxWidth="lg">
            <Breadcrumbs 
              items={[
                { label: 'Accueil', href: '/' },
                { label: 'Missions', href: '/missions' }
              ]}
            />
            
            <Typography 
              variant="h1" 
              className="missions-title"
              sx={{ 
                textAlign: 'center',
                mb: 4,
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 'bold',
                color: 'text.primary'
              }}
            >
              Trouvez votre mission sportive
            </Typography>
            
            <SearchBar onSearch={handleSearch} />
          </Container>
        </Box>
        
        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box className="missions-content">
            {/* Filters and Sort Controls */}
            <Box className="missions-controls" sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                  {paginatedMissions.length} mission{paginatedMissions.length > 1 ? 's' : ''} trouvée{paginatedMissions.length > 1 ? 's' : ''}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {isMobile && (
                    <Button
                      variant="outlined"
                      startIcon={<FilterList />}
                      onClick={() => setFiltersOpen(true)}
                      className="filter-button"
                    >
                      Filtres
                    </Button>
                  )}
                  
                  <Button
                    variant="outlined"
                    startIcon={<MapIcon />}
                    onClick={() => setMapOpen(!mapOpen)}
                    className="map-button"
                  >
                    {mapOpen ? 'Masquer' : 'Voir'} la carte
                  </Button>
                  
                  <select 
                    value={sortBy} 
                    onChange={(e) => dispatch(setSortBy(e.target.value))}
                    className="sort-select"
                  >
                    <option value="relevance">Pertinence</option>
                    <option value="date">Date</option>
                    <option value="rate-asc">Tarif croissant</option>
                    <option value="rate-desc">Tarif décroissant</option>
                  </select>
                </Box>
              </Box>
            </Box>
            
            <Grid container spacing={3}>
              {/* Filters Panel (Desktop) */}
              {!isMobile && (
                <Grid item lg={3}>
                  <FiltersPanel />
                </Grid>
              )}
              
              {/* Main Content Area */}
              <Grid item xs={12} lg={!isMobile ? 9 : 12}>
                {/* Map View */}
                {mapOpen && (
                  <Box sx={{ mb: 3 }}>
                    <MapView missions={paginatedMissions} onMissionClick={handleMissionClick} />
                  </Box>
                )}
                
                {/* Error State */}
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}
                
                {/* Missions Grid */}
                <Grid container spacing={3}>
                  {loading ? (
                    renderSkeletonCards()
                  ) : paginatedMissions.length > 0 ? (
                    paginatedMissions.map((mission) => (
                      <Grid item xs={12} sm={6} lg={4} key={mission.id}>
                        <MissionCard
                          mission={mission}
                          onClick={() => handleMissionClick(mission)}
                          onFavorite={() => handleFavoriteToggle(mission.id)}
                          onApply={() => handleApply(mission.id)}
                          onShare={() => handleShare(mission)}
                        />
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box className="no-results" sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                          Aucune mission trouvée
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          Essayez de modifier vos critères de recherche
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                
                {/* Pagination */}
                {!loading && paginatedMissions.length > 0 && pagination.totalPages > 1 && (
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Container>
        
        {/* Mobile Filters Drawer */}
        <Drawer
          anchor="bottom"
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          className="mobile-filters-drawer"
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Filtres
            </Typography>
            <FiltersPanel onClose={() => setFiltersOpen(false)} />
          </Box>
        </Drawer>
        
        {/* Mission Detail Modal */}
        <MissionDetailModal
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          mission={selectedMission}
          onApply={handleApply}
          onFavorite={handleFavoriteToggle}
          onShare={handleShare}
        />
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Missions;